// src/components/charts/assistant/TimeSeriesChart.tsx
/**
 * Assistant Time Series Chart
 * 
 * A time series chart optimized for the assistant's dark card UI with:
 * - Custom tooltip styling with colored badges
 * - Support for stacking (normal and 100%)
 * - Multiple chart styles (line, area, bar)
 * - Date parsing for YYYYMMDD formats
 */

import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { parseDateToTimestamp, formatDateLabel } from "@/utils/charts/dataUtils";
import { 
  ASSISTANT_CHART_THEME, 
  ASSISTANT_CHART_HEIGHT,
  getFormatter,
  type FormatterOptions,
  type StackingMode,
  type ChartStyle,
} from "./theme";

// Re-export types for backwards compatibility
export type { StackingMode, ChartStyle };

export interface AssistantTimeSeriesChartProps {
  /** Date values (YYYYMMDD integers, timestamps, or ISO strings) */
  dates: (string | number)[];
  /** Data series */
  series: Array<{ name: string; values: number[] }>;
  /** Value formatting options */
  formatOptions?: FormatterOptions;
  /** Y-axis label */
  yAxisName?: string;
  /** Show legend */
  showLegend?: boolean;
  /** Chart style */
  style?: ChartStyle;
  /** Stacking mode */
  stacking?: StackingMode;
  /** Use logarithmic Y-axis scale */
  logScale?: boolean;
  /** Chart height (defaults to ASSISTANT_CHART_HEIGHT) */
  height?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const AssistantTimeSeriesChartInner: React.FC<AssistantTimeSeriesChartProps> = ({ 
  dates, 
  series, 
  formatOptions, 
  yAxisName, 
  showLegend = true, 
  style = "line", 
  stacking = "none", 
  logScale = false,
  height = ASSISTANT_CHART_HEIGHT,
}) => {
  const formatter = getFormatter(formatOptions);
  const isStacked = stacking === "stacked" || stacking === "100_percent";
  const is100Percent = stacking === "100_percent";
  
  // Pre-process dates: parse YYYYMMDD or other formats into display labels
  const dateLabels = useMemo(() => {
    const needsYear = dates.length > 90 || (() => {
      if (dates.length < 2) return false;
      const firstStr = String(dates[0]);
      const lastStr = String(dates[dates.length - 1]);
      if (/^\d{8}$/.test(firstStr) && /^\d{8}$/.test(lastStr)) {
        const firstYear = firstStr.slice(0, 4);
        const lastYear = lastStr.slice(0, 4);
        return firstYear !== lastYear;
      }
      return false;
    })();
    
    return dates.map(d => {
      if (typeof d === 'number' || (typeof d === 'string' && /^\d{8}$/.test(d))) {
        const numDate = typeof d === 'number' ? d : parseInt(d, 10);
        const ts = parseDateToTimestamp(numDate);
        if (needsYear) {
          const date = new Date(ts);
          const month = date.toLocaleDateString('en-US', { month: 'short' });
          const year = String(date.getFullYear()).slice(2);
          return `${month} '${year}`;
        }
        return formatDateLabel(ts, 'short');
      }
      if (typeof d === 'string' && d.includes('T')) {
        const ts = parseDateToTimestamp(d);
        if (needsYear) {
          const date = new Date(ts);
          const month = date.toLocaleDateString('en-US', { month: 'short' });
          const year = String(date.getFullYear()).slice(2);
          return `${month} '${year}`;
        }
        return formatDateLabel(ts, 'short');
      }
      return String(d);
    });
  }, [dates]);

  // For 100% stacking, normalize values at each date point
  const normalizedSeries = useMemo(() => {
    if (!is100Percent) return series;
    
    const numDates = dates.length;
    const result: Array<{ name: string; values: number[] }> = series.map(s => ({
      name: s.name,
      values: [...s.values],
    }));
    
    for (let i = 0; i < numDates; i++) {
      const total = series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0);
      if (total > 0) {
        result.forEach((s, idx) => {
          s.values[i] = ((series[idx]?.values[i] ?? 0) / total) * 100;
        });
      }
    }
    
    return result;
  }, [series, dates.length, is100Percent]);

  const rawDates = dates;
  
  const option = useMemo(() => {
    const colors = ASSISTANT_CHART_THEME.lineColors;
    const displayFormatter = is100Percent ? (v: number) => `${v.toFixed(1)}%` : formatter;
    
    return {
      backgroundColor: ASSISTANT_CHART_THEME.background,
      color: colors,
      tooltip: {
        trigger: "axis" as const,
        backgroundColor: ASSISTANT_CHART_THEME.tooltip.background,
        borderColor: ASSISTANT_CHART_THEME.tooltip.border,
        borderWidth: 1,
        padding: [10, 14],
        textStyle: {
          color: ASSISTANT_CHART_THEME.text.primary,
          fontSize: 12,
        },
        formatter: (params: unknown) => {
          const items = params as Array<{ 
            axisValue: string; 
            seriesName: string; 
            value: number; 
            color: string;
            dataIndex: number;
          }>;
          if (!items?.length) return "";
          
          const idx = items[0]!.dataIndex;
          const rawDate = rawDates[idx];
          let dateStr = items[0]!.axisValue;
          if (rawDate !== undefined) {
            const numDate = typeof rawDate === 'number' ? rawDate : 
              (typeof rawDate === 'string' && /^\d{8}$/.test(rawDate)) ? parseInt(rawDate, 10) : null;
            if (numDate) {
              const ts = parseDateToTimestamp(numDate);
              dateStr = formatDateLabel(ts, 'medium');
            }
          }
          
          let html = `<div style="font-weight:500;margin-bottom:6px;color:rgba(255,255,255,0.85)">${dateStr}</div>`;
          items.forEach(item => {
            const val = item.value;
            html += `
              <div style="display:flex;align-items:center;gap:6px;margin-top:4px">
                <span style="display:inline-block;width:8px;height:3px;border-radius:1px;background:${item.color}"></span>
                <span style="color:rgba(255,255,255,0.6)">${item.seriesName}:</span>
                <span style="color:rgba(255,255,255,0.9);font-weight:500">${displayFormatter(val)}</span>
              </div>
            `;
          });
          return html;
        },
      },
      legend: showLegend && normalizedSeries.length > 1 ? {
        data: normalizedSeries.map(s => s.name),
        bottom: 0,
        textStyle: {
          color: ASSISTANT_CHART_THEME.text.muted,
          fontSize: 11,
        },
        icon: "roundRect",
        itemWidth: 12,
        itemHeight: 3,
        itemGap: 16,
      } : undefined,
      grid: {
        left: 8,
        right: 16,
        top: yAxisName ? 28 : 12,
        bottom: showLegend && normalizedSeries.length > 1 ? 40 : 24,
        containLabel: true,
      },
      xAxis: {
        type: "category" as const,
        data: dateLabels,
        axisLabel: {
          color: ASSISTANT_CHART_THEME.text.muted,
          fontSize: 10,
        },
        axisTick: { show: false },
        axisLine: { show: false },
      },
      yAxis: {
        type: logScale ? "log" as const : "value" as const,
        ...(yAxisName ? { name: yAxisName, nameLocation: "end" as const } : {}),
        ...(is100Percent ? { max: 100, min: 0 } : {}),
        nameTextStyle: {
          color: ASSISTANT_CHART_THEME.text.muted,
          fontSize: 10,
          align: "left" as const,
          padding: [0, 0, 4, 50],
        },
        axisLabel: {
          color: ASSISTANT_CHART_THEME.text.muted,
          fontSize: 10,
          formatter: displayFormatter,
        },
        splitLine: {
          lineStyle: {
            color: ASSISTANT_CHART_THEME.grid.line,
            type: "dashed" as const,
          },
        },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: normalizedSeries.map((s, i) => {
        const color = colors[i % colors.length]!;
        const showArea = style === "area" || isStacked;
        
        return {
          name: s.name,
          type: style === "bar" ? "bar" as const : "line" as const,
          data: s.values,
          smooth: style !== "bar" ? 0.3 : undefined,
          symbol: "none",
          stack: isStacked ? "total" : undefined,
          lineStyle: style !== "bar" ? {
            width: isStacked ? 1 : 2,
            color,
          } : undefined,
          itemStyle: style === "bar" ? {
            color,
            borderRadius: [4, 4, 0, 0],
          } : undefined,
          areaStyle: showArea ? {
            color: isStacked ? color : {
              type: "linear" as const,
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: `${color}40` },
                { offset: 1, color: `${color}05` },
              ],
            },
            opacity: isStacked ? 0.85 : 1,
          } : undefined,
        };
      }),
      animationDuration: 500,
      animationEasing: "cubicOut" as const,
    };
  }, [dateLabels, rawDates, normalizedSeries, yAxisName, showLegend, formatter, style, isStacked, is100Percent, logScale]);

  return (
    <ReactECharts
      option={option}
      style={{ height, width: "100%" }}
      opts={{ renderer: "canvas" }}
      notMerge
    />
  );
};

// Custom comparison for memo - only re-render if data actually changed
function arePropsEqual(
  prev: AssistantTimeSeriesChartProps,
  next: AssistantTimeSeriesChartProps
): boolean {
  // Quick checks for primitives
  if (
    prev.yAxisName !== next.yAxisName ||
    prev.showLegend !== next.showLegend ||
    prev.style !== next.style ||
    prev.stacking !== next.stacking ||
    prev.logScale !== next.logScale ||
    prev.height !== next.height
  ) {
    return false;
  }

  // Check dates array
  if (prev.dates.length !== next.dates.length) return false;
  if (prev.dates !== next.dates && prev.dates.some((d, i) => d !== next.dates[i])) {
    return false;
  }

  // Check series array
  if (prev.series.length !== next.series.length) return false;
  if (prev.series !== next.series) {
    for (let i = 0; i < prev.series.length; i++) {
      const p = prev.series[i]!;
      const n = next.series[i]!;
      if (p.name !== n.name || p.values.length !== n.values.length) return false;
      // For performance, just check if values array reference changed
      // and first/last values match (good enough for streaming updates)
      if (p.values !== n.values) {
        if (p.values[0] !== n.values[0] || p.values[p.values.length - 1] !== n.values[n.values.length - 1]) {
          return false;
        }
      }
    }
  }

  // Check formatOptions
  if (prev.formatOptions !== next.formatOptions) {
    if (!prev.formatOptions || !next.formatOptions) return false;
    if (
      prev.formatOptions.unit !== next.formatOptions.unit ||
      prev.formatOptions.isChangeMetric !== next.formatOptions.isChangeMetric
    ) {
      return false;
    }
  }

  return true;
}

const AssistantTimeSeriesChart = React.memo(AssistantTimeSeriesChartInner, arePropsEqual);

export default AssistantTimeSeriesChart;

